import { useContext, useEffect, useMemo, useState, useCallback } from "react"
import Aside from "../../../components/Aside"
import Footer from "../../../components/Footer"
import Header from "../../../components/Header"
import { useCurrentAccount } from "../../../hooks/useCurrentAccount"
import { DateUtil } from "../../../utils/date"
import { ReportService } from "../../../services/ReportsService"
import CustomDataTable from "../../../components/CustomDataTable"
import PieChart from "../../../components/PieChart"
import LineChart from "../../../components/LineChart"
import Tooltip from "../../../components/Tooltip"
import { AlertService } from "../../../services/Alert"
import { AppContext } from "../../../context/AppContext"
import { useTranslation } from "react-i18next"
import * as XLSX from 'xlsx';
import { ChannelUtil } from "../../../utils/channel"
import useTokenApi from "../../../hooks/useTokenApi"
import FullPieChart from "../../../components/FullPieChart"
import { ColorUtil } from "../../../utils/color"
import useRequest from "../../../hooks/useRequest"
import LoaderSmall from "../../../components/LoaderSmall"
import usePermission from "../../../hooks/usePermission"

function ReportTicketsPage() {

    const { t }= useTranslation();
    const {call, isRequesting} = useRequest();
    const { setShowTicketDetail } = useContext(AppContext);
    const { account } = useCurrentAccount();
    const { tokenApi } = useTokenApi();
    const hasActionsAccess = usePermission('reports.actions'); 
    const hasDownloadAccess = usePermission('reports.download');
    const [rows, setRows] = useState([])
    const [filteredRows, setFilteredRows] = useState<any[]>([]);
    const [tipifications, setTipifications] = useState<{ value: string; label: string }[]>([]);
    const [categories, setCategories] = useState({
        labels: [],
        data: [],
    })
    const [distibution, setDistibution] = useState({
        labels: [],
        data: [],
    })
    const [traffic, setTraffic] = useState([])
    const [avgResolve, setAvgResolve] = useState('00:00')
    const [avgWait, setAvgWait] = useState('00:00')

    const [filterRangeDates, setFilterRangeDates] = useState({ start: null, end: null })

    const [fullPieChartData, setFullPieChartData] = useState({show: false, data: [], title: ''})

    const columns = [
        {
            name: t("reports.tickets.ticket-id"),
            sortable: true,
            cell: row => {

                let ticket = row.ticket_id.split('-')[0]

                return <Tooltip title={row.ticket_id} className="d-grid gap-2">
                    <p className="text-nowrap  link-underline cursor-pointer mb-0" onClick={() => copyToClipboard(row.ticket_id)}>{ticket}</p>
                </Tooltip>
            },
        },
        {
            name: t("reports.tickets.external-id"),
            sortable: true,
            selector: row => row.external_id || <span className="badge bg-soft-danger text-danger">Sin información</span>,
        },
        {
            name: t("reports.tickets.customer-base"),
            sortable: true,
            cell: row => <p className="text-nowrap mb-0">{row.base || <span className="badge bg-soft-danger text-danger">Sin información</span> }</p>,
        },
        {
            name: t("reports.tickets.subaccount"),
            sortable: true,
            selector: row => row.sub_account,
        },
        {
            name: t("reports.tickets.number"),
            sortable: true,
            selector: row => row.number,
        },
        {
            name: t("reports.tickets.name"),
            sortable: true,
            cell: row => <p className="text-nowrap mb-0">{row.name_client}</p>,
        },
        {
            name: t("reports.tickets.agent"),
            sortable: true,
            selector: row => row.agent_name,
        },
        {
            name: t("reports.tickets.channel"),
            sortable: true,
            center: true,
            cell: row => {
                return (
                    <div className="text-nowrap">
                        {(() => {
                            switch (row.app.toUpperCase()) {
                                case 'WA-BUSINESS':
                                    return <div><span className="badge bg-soft-primary text-primary"> <i className="bi bi-whatsapp"></i> {t("platform.channels.links-page.wa-business")} </span></div>
                                case 'RCS':
                                    return <div><span className="badge badge-soft-rcs"> <i className="bi bi-chat-left-text"></i> {t("platform.channels.links-page.rcs")}</span></div>
                                default:
                                    return <div></div>;
                            }
                        })()}
                    </div>
                )
            },
        },
        {
            name: t("reports.tickets.initial-category"),
            sortable: true,
            cell: row => <p className="text-nowrap mb-0">{row.typing_intermediate || <span className="badge bg-soft-danger text-danger">Sin información</span> } </p>,
        },
        {
            name: t("reports.tickets.closure-category"),
            sortable: true,
            cell: row => <p className="text-nowrap mb-0">{row.typing_close }</p>,
        },
        {
            name: t("reports.tickets.type-contact"),
            sortable: true,
            cell: row => <p className="text-nowrap mb-0">{row.type_contact}</p>,
        },
        {
            name: t("reports.tickets.initial-date"),
            sortable: true,
            cell: row => <p className="text-nowrap mb-0">{row.date_init}</p>,
        },
        {
            name: t("reports.tickets.assignment-date"),
            sortable: true,
            cell: row => <p className="text-nowrap mb-0">{row.date_assigment}</p>,
        },
        {
            name: t("reports.tickets.closure-date"),
            sortable: true,
            cell: row => <p className="text-nowrap mb-0">{row.date_close}</p>,
        },
        {
            name: t("reports.tickets.duration"),
            sortable: true,
            cell: row => <p className="text-nowrap mb-0">{row.duration}</p>,
        },
        
        ...(hasActionsAccess ? [{
            name: t("reports.tickets.actions"),
            sortable: true,
            cell: row => (
              <div className="btn-group" role="group">
                <button className="btn btn-white btn-sm" onClick={() => setShowTicketDetail({ ticketID: row.ticket_id, show: true })}>
                  <i className="bi-chat-square-text-fill me-2"></i> {t("reports.tickets.btn-see")}
                </button>
              </div>
            ),
          }] : []),
    ];

    const copyToClipboard = async (text) => {
        await navigator.clipboard.writeText(text);

        AlertService.toastSuccess('Texto copiado', 2000)
    }

    const formatCategory = (category?: string) => {
        if (+account.account_id === 188 && category && category.toLowerCase() === 'convenio del bot') {
          return `99 ${category}`;
        }
    
    
        return category;
    }

    const handleRowsFiltered = useCallback((filteredRows: any) => {
        setFilteredRows(prevRows => {
            if (JSON.stringify(prevRows) !== JSON.stringify(filteredRows)) {
                return filteredRows;
            }
            return prevRows;
        });
    }, []);

    const downloadReport = (isCSV = false) => {
        const skipKeys = [
          'id',
          'id_externo',
          'number',
          'normalPhone',
          'app',
          'name_client',
          'name_agent',
          'first_message_agent',
          'category',
          'date_init',
          'date_assignment',
          'date',
          'base',
          'duration',
          'type_send',
        ];
    
        const HEADER = [
          'ID',
          'ID EXTERNO',
          'Chat ID',
          'Número',
          'Canal',
          'Nombre Cliente',
          'Agente',
          'Primer mensaje del agente',
          'Categoría',
          'Fecha de inicio',
          'Fecha de asignación',
          'Fecha de cierre',
          'Base',
          'Duración',
          'Tipo de envío'
        ];
    
        const HEADER_COPPEL = [
          'Numero de cliente',
          'Id de agente',
          'etiqueta',
          'base_id',
          'sub_cuenta',
          'Importe',
          'Plazo',
          'Fecha',
          'Hora',
          '¿Quién contesto?',
          '¿Razón de incumplimiento?'
        ];
    
        const data = filteredRows.map(row => {
    
          let forms = {};

          if (+account.account_id === 188) {
            forms = HEADER_COPPEL.map( h => ({ [h]: row[h] || '-' }))
            .reduce((a, b) => Object.assign(a, b), {});
          } else {
            forms = Object.keys(row)
              .filter(v => skipKeys.indexOf(v) === -1)
              .map(v => {
                let value = row[v];
            
                if (+account.account_id === 188 && v === 'Importe') {
                  const regex = /[^0-9.]/g;
                  value = value.replace(regex, '');
                }
    
    
                if (value && typeof value === 'object') {
                  if (value.tipo === 'externo') {
                    value = ReportService.getURLPublicFile(value.nombre);
                  } else if (value.tipo === 'local') {
                    value = ReportService.getURLExternalFile(row.id, value.nombre);
                  }
                }
    
                return { [v]: value || '-' };
              })
              .reduce((a, b) => Object.assign(a, b), {});
          }

          const fields = {
            ID: row.ticket_id,
            'ID EXTERNO': row.external_id ? row.external_id : '-',
            'Chat ID': row.number,
            Número: row.number,
            Canal: ChannelUtil.format(row.app, row.category),
            'Nombre Cliente': row.name_client,
            Agente: row.agent_name,
            'Primer mensaje del agente': +account.account_id === 188 ? row.first_message_agent || row.date_init : row.first_message_agent,
            Categoría: formatCategory(row.typing_close),
            'Fecha de inicio': row.date_init,
            'Fecha de asignación': row.date_assigment,
            'Fecha de cierre': row.date_close,
            Base: row.base,
            Duración: row.duration,
            'Tipo de envío': row.type_send,
          };
    
          return Object.assign(fields, forms);
        });
        const wb = XLSX.utils.book_new();
    
        const ws = XLSX.utils.json_to_sheet(data, {
          header: HEADER
        });
    
        if (isCSV) {
          const csv = XLSX.utils.sheet_to_csv(ws, {FS: '|'});
    
          const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-18' });
          const link = document.createElement('a');
          if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', 'reporte.csv');
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
    
        } else {
          XLSX.utils.book_append_sheet(wb, ws);
          return XLSX.writeFile(wb, 'reporte.xlsx');
        }
    }

    const downloadConversationsCSV = () => {
        const url = ReportService.downloadCSV({
          token_api: tokenApi,
          accounts_id: account.account_id,
          date_init: DateUtil.ISOFormat(filterRangeDates.start),
          date_end: DateUtil.ISOFormat(filterRangeDates.end),
          number: '',
          categories:  ''
        });
    
        const win = window.open(url, '_blank');
        win.focus();
    }
    

    const getReport = async () => {

        if (filterRangeDates.start && filterRangeDates.end) {
            const start = filterRangeDates.start.format('YYYY-MM-DD')
            const end = filterRangeDates.end.format('YYYY-MM-DD')

            const { data } = await call(() => ReportService.getReportTickets(account.id, start, end));
            setCategories({
                labels: data.categories.map( c => c.categoria),
                data: data.categories.map( c => c.total),
            })

            setDistibution({
                labels: ['Agente', 'Bot', 'Sin asignar'],
                data: [data.distibution.agent, data.distibution.bot, data.distibution.dont_assigned]
            })

            const tipificationOptions = data.tipifications.map((tipification: any) => ({
                value: tipification,
                label: tipification
            }));

            setTipifications(tipificationOptions);
            setTraffic(data.traffic)
            setAvgResolve(data.avg_resolve)
            setAvgWait(data.avg_wait)
            setRows(data.tickets)
        }
    }

    useEffect(() => {
        getReport()
    }, [filterRangeDates])

    const backgroundColorCustomers = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d")
        let gradient = ctx.createLinearGradient(canvas.width / 2, 0, canvas.width / 2, canvas.height);
        gradient.addColorStop(0, 'rgba(55, 125, 255, 0.5)')
        gradient.addColorStop(1, 'rgba(55, 125, 255, 0.0)')

        return gradient
    }

    const backgroundColorBusiness = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d")
        let gradient = ctx.createLinearGradient(canvas.width / 2, 0, canvas.width / 2, canvas.height);
        gradient.addColorStop(0, 'rgba(0, 201, 219, 0.5)')
        gradient.addColorStop(1, 'rgba(0, 201, 219, 0.0)')

        return gradient
    }

    const heightRequesting = useMemo(()=> {return isRequesting ? 'h-200':''},[isRequesting]);
    return (
        <>
            <Header />
            <Aside />

            <main id="content" role="main" className="main">

                <div className="content container-fluid">

                    <div className="page-header">
                        <div className="row align-items-center">
                            <div className="col-sm mb-2 mb-sm-0">
                                <nav aria-label="breadcrumb">
                                    <ol className="breadcrumb breadcrumb-no-gutter">
                                        <li className="breadcrumb-item "><span className="breadcrumb-link" >{t("reports.operation-title")}</span></li>
                                        <li className="breadcrumb-item " aria-current="page">{t("reports.reports-title")}</li>
                                        <li className="breadcrumb-item active" aria-current="page">{t("reports.tickets.tickets-title")}</li>
                                    </ol>
                                </nav>
                                <h1 className="page-header-title"><i className="bi-grid-1x2 nav-icon"></i> {t("reports.reports-title")} - {t("reports.tickets.tickets-title")}</h1>
                            </div>

                        </div>
                    </div>

                    <div className="mb-3 mb-lg-5">
                        <div className={ `relative ${heightRequesting}`}>
                        {
                            isRequesting ? <LoaderSmall/> :
                        <>
                        <div className="row  gx-lg-6">
                            <div className="col-lg-12 mb-5">
                                    <div className="card card-sm">
                                        <div className="card-body">
                                            <div className="d-flex">
                                                <div className="flex-grow-1">
                                                    <h6 className="card-subtitle mb-3">{t("reports.tickets.traffic")}</h6>
                                                    <div className="w-full">
                                                        <LineChart 
                                                            labels={traffic.map( t => t.day)}
                                                            width={'100%'}
                                                            datasets={[
                                                                {
                                                                    label: t("reports.tickets.messages-user"),
                                                                    data: traffic.map( t => t.clients),
                                                                    backgroundColor: backgroundColorCustomers(),
                                                                    borderColor: "#377dff",
                                                                    borderWidth: 2,
                                                                    tension: 0.4,
                                                                    pointRadius: 2,
                                                                    pointHoverRadius: 2,
                                                                    fill: true
                                                                },
                                                                {
                                                                    label: t("reports.tickets.messages-company"),
                                                                    data: traffic.map( t => t.enterpraice),
                                                                    backgroundColor: backgroundColorBusiness(),
                                                                    borderColor: "#00c9db",
                                                                    borderWidth: 2,
                                                                    tension: 0.4,
                                                                    pointRadius: 2,
                                                                    pointHoverRadius: 2,
                                                                    fill: true
                                                                },
                                                            ]}
                                                            legendDisplay={false}
                                                        />
                                                    </div>

                                                </div>

                                                {/* <span className="icon bg-soft-info-alternative  text-info-alternative icon-sm icon-circle ms-3">
                                                    <i className="bi-clipboard-check-fill"></i>
                                                </span> */}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                        </div>

                        <div className="row  gx-lg-6">
                            
                            <div className="col-lg-3 mb-md-3 mb-3">
                                <div className="card card-sm h-full" >
                                    <div className="card-body">
                                        <div className="d-flex h-full">
                                            <div className="flex-grow-1 h-full">
                                                <h6 className="card-subtitle mb-0">{t("reports.tickets.wait-time")}</h6>
                                                <div className="d-flex  h-full justify-content-center align-items-center">
                                                    <h1 className="card-title">{DateUtil.formatTime(avgWait)}</h1>
                                                </div>

                                            </div>

                                            {/* <span className="icon bg-soft-info-alternative  text-info-alternative icon-sm icon-circle">
                                                <i className="bi-person-workspace"></i>
                                            </span> */}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="col-lg-3 mb-md-3 mb-3">
                                <div className="card card-sm h-full" >
                                    <div className="card-body">
                                        <div className="d-flex h-full">
                                            <div className="flex-grow-1 h-full">
                                                <h6 className="card-subtitle mb-0">{t("reports.tickets.closure-time")}</h6>
                                                <div className="d-flex  h-full justify-content-center align-items-center">
                                                    <h1 className="card-title">{DateUtil.formatTime(avgResolve)}</h1>
                                                </div>

                                            </div>

                                            {/* <span className="icon bg-soft-info-alternative  text-info-alternative icon-sm icon-circle">
                                                <i className="bi-person-workspace"></i>
                                            </span> */}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-3 mb-md-3 mb-3">
                                <div className="card card-sm h-full">
                                    <div className="card-body">
                                        <div className="d-flex">
                                            <div className="flex-grow-1">
                                                <div className="mb-3 d-flex align-items-center">
                                                    <h6 className="card-subtitle ">{t("reports.tickets.categories")}</h6>
                                                    <button title="chart" type="button" className="btn btn-link ms-auto p-0" onClick={() => setFullPieChartData({ title: t("reports.tickets.categories"),show: true, data: categories.labels.map( (c, i) => ({ label: c, value: categories.data[i ]}))})}><i className="bi bi-arrows-fullscreen"></i></button>
                                                </div>
                                                
                                                <div>
                                                    <PieChart 
                                                        datasetName='Categorías'
                                                        dataset={{
                                                            labels: categories.labels,
                                                            data: categories.data,
                                                            backgroundColor: categories.labels.map( l => ColorUtil.stringToRGB(l)),
                                                        }}
                                                    />
                                                </div>

                                            </div>

                                            {/* <span className="icon bg-soft-info-alternative  text-info-alternative icon-sm icon-circle ms-3">
                                                <i className="bi-clipboard-x-fill"></i>
                                            </span> */}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="col-lg-3 mb-3">
                                <div className="card card-sm h-full">
                                    <div className="card-body">
                                        <div className="d-flex">
                                            <div className="flex-grow-1">
                                                
                                                <div className="mb-3 d-flex align-items-center">
                                                    <h6 className="card-subtitle">{t("reports.tickets.distribution")}</h6>
                                                    <button title="chart" type="button" className="btn btn-link ms-auto p-0" onClick={() => setFullPieChartData({ title: t("reports.tickets.distribution"),show: true, data: distibution.labels.map( (c, i) => ({ label: c, value: distibution.data[i ]}))})}><i className="bi bi-arrows-fullscreen"></i></button>
                                                </div>
                                                    <div>
                                                        <PieChart 
                                                            datasetName='Distribución de tickets'
                                                            dataset={{
                                                                labels: distibution.labels,
                                                                data: distibution.data,
                                                                // backgroundColor: ['#0a595f','#13A1AB','#ED4C78']
                                                                backgroundColor: distibution.labels.map( l => ColorUtil.stringToRGB(l)),
                                                            }}
                                                        />
                                                    </div>

                                            </div>

                                            {/* <span className="icon bg-soft-info-alternative  text-info-alternative icon-sm icon-circle ms-3">
                                                <i className="bi-chat-square-dots-fill"></i>
                                            </span> */}
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                        </>
                        }
                        </div>
                    </div>


                    <CustomDataTable
                        columns={columns}
                        showDates
                        data={rows}
                        hideAccountsFilter
                        isLoading={isRequesting}
                        filterDay={0}
                        onRowsFiltered={handleRowsFiltered}
                        extraFilter={tipifications}
                        onExport={hasDownloadAccess ? (format) => downloadReport(format === 'csv') : null}
                        extraExportActions={
                            <>
                                <button className="dropdown-item" onClick={downloadConversationsCSV} >
                                    <img className="avatar avatar-xss avatar-4x3 me-2" src="/assets/svg/components/placeholder-csv-format.svg" alt="Image Description" />
                                    Conversaciones (.csv)
                                </button>
                            </>
                        }
                        onDatesChange={(range) => setFilterRangeDates(range)}
                        filter={(row, term) => {
                            if (!term) return true;

                            return (
                                `${row.ticket_id}`.match(new RegExp(`${term}`, 'i')) || 
                                `${row.external_id}`.match(new RegExp(`${term}`, 'i')) || 
                                `${row.base}`.match(new RegExp(`${term}`, 'i')) || 
                                `${row.sub_account}`.match(new RegExp(`${term}`, 'i')) || 
                                `${row.number}`.match(new RegExp(`${term}`, 'i')) || 
                                `${row.name_client}`.match(new RegExp(`${term}`, 'i')) || 
                                `${row.agent_name}`.match(new RegExp(`${term}`, 'i')) || 
                                `${row.app}`.match(new RegExp(`${term}`, 'i')) || 
                                `${row.typing_intermediate}`.match(new RegExp(`${term}`, 'i')) || 
                                `${row.typing_close}`.match(new RegExp(`${term}`, 'i')) || 
                                `${row.type_contact}`.match(new RegExp(`${term}`, 'i')) 
                            )
                        }}
                    />

                </div>
                <Footer />
            </main>

            <FullPieChart 
                show={fullPieChartData.show}
                data={fullPieChartData.data}
                title={fullPieChartData.title}
                onRequestClose={() => setFullPieChartData({ show: false, data: [], title: ''})}
            />
        </>
    )
}
export default ReportTicketsPage